import { GroupCondition } from '@/api/enums';
import { getQueryTransformation, group, like, nested } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { CampaignUserResultMessages } from '@/client/campaigns';
import { FilterNamesEnum, getFiltersFromColumns } from '@/client/helpers';
import { UsersReportType, UsersSummaryReport } from '@/client/reports';
import { Actions, Subjects } from '@/client/users';
import {
  RedirectPaths,
  RedirectPathsEnum,
  TableNamesEnum,
} from '@/common/constants';
import {
  DataTable,
  DataTableAPIColumnsMultiSelect,
  DataTableColumnType,
  DataTableFilters,
  DataTableToolbar,
  FilterTypeEnum,
} from '@/components/tables/crud';
import { useUsersSummaryReport } from '@/hooks/query';
import { useQueryParams } from '@/hooks/query.hook';
import { useAppSelector } from '@/hooks/store';
import { useTable } from '@/hooks/table.hook';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermission } from '@/hooks/usePermission';
import SummaryReportBlankPageImg from '@/images/blank-pages/summary-blank-page.svg';
import { ReportsActionHeader } from '@/pages/reports/ReportsActionHeader';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { DateFormats } from '@/system-settings/enums';
import { BlankPage } from '@/ui/blank-page/BlankPage';
import { FormatDate } from '@/ui/date';
import { FlexContainer } from '@/ui/styled-ui';
import { branchAdminCheck, getCampaignEventStatuses } from '@/utils/helpers';
import { getBranchName } from '@/utils/helpers/branches.helper';
import { t } from 'i18next';
import { debounce, uniqueId } from 'lodash';
import { DataTableRowClickEvent } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const StyledDataTable = styled(DataTable)`
  &.p-datatable {
    th[role='columnheader']:last-child .p-column-header-content {
      justify-content: flex-start;
    }

    td[role='cell']:last-child {
      text-align: left;
    }
  }

  .p-datatable-table {
    width: 100%;
  }

  .p-datatable-wrapper .p-datatable-table .icon-column {
    text-align: center !important;

    .p-column-title {
      margin: auto;

      &:before {
        display: block;
        width: 24px;
        height: 24px;
      }
    }

    &.enrolled-materials-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16.9038 20.0577L20 18.1154L16.9038 16.1731V20.0577ZM6.76923 8.73078H17.2308V7.73078H6.76923V8.73078ZM18 22.1154C16.8859 22.1154 15.9407 21.7273 15.1644 20.951C14.3881 20.1747 14 19.2295 14 18.1154C14 17.0013 14.3881 16.0561 15.1644 15.2798C15.9407 14.5035 16.8859 14.1154 18 14.1154C19.1141 14.1154 20.0593 14.5035 20.8356 15.2798C21.6119 16.0561 22 17.0013 22 18.1154C22 19.2295 21.6119 20.1747 20.8356 20.951C20.0593 21.7273 19.1141 22.1154 18 22.1154ZM4 20.7693V5.61537C4 5.16794 4.15737 4.78685 4.4721 4.4721C4.78685 4.15737 5.16794 4 5.61538 4H18.3846C18.8321 4 19.2132 4.15737 19.5279 4.4721C19.8426 4.78685 20 5.16794 20 5.61537V11.5596C19.8372 11.4994 19.6756 11.4481 19.5154 11.4058C19.3551 11.3635 19.1833 11.3301 19 11.3058V5.61537C19 5.46154 18.9359 5.32052 18.8077 5.1923C18.6795 5.0641 18.5385 5 18.3846 5H5.61538C5.46154 5 5.32052 5.0641 5.1923 5.1923C5.0641 5.32052 5 5.46154 5 5.61537V19.05H11.3442C11.3891 19.3231 11.4478 19.5904 11.5202 19.8519C11.5926 20.1135 11.6936 20.3628 11.8231 20.6L11.7885 20.6346L10.6539 19.8077L9.3077 20.7693L7.96152 19.8077L6.61537 20.7693L5.26923 19.8077L4 20.7693ZM6.76923 16.2692H11.4788C11.516 16.0859 11.5622 15.9141 11.6173 15.7539C11.6724 15.5936 11.7365 15.432 11.8096 15.2692H6.76923V16.2692ZM6.76923 12.5H14.0808C14.4064 12.2423 14.7561 12.0272 15.1298 11.8548C15.5035 11.6824 15.9026 11.5641 16.3269 11.5H6.76923V12.5Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.not-completed-materials-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.4464 12.6463L14.6502 11.8501C14.8002 10.977 14.5528 10.2213 13.9079 9.58281C13.263 8.94434 12.5105 8.70011 11.6502 8.85011L10.8541 8.05396C11.022 7.98473 11.1973 7.9328 11.38 7.89819C11.5627 7.86357 11.7695 7.84626 12.0002 7.84626C13.0195 7.84626 13.8832 8.20043 14.5916 8.90876C15.2999 9.61709 15.6541 10.4809 15.6541 11.5001C15.6541 11.7309 15.6368 11.944 15.6021 12.1395C15.5675 12.3351 15.5156 12.504 15.4464 12.6463ZM18.6079 15.7193L17.8502 15.0501C18.4836 14.5668 19.0461 14.0376 19.5377 13.4626C20.0294 12.8876 20.4502 12.2334 20.8002 11.5001C19.9669 9.81678 18.7711 8.47928 17.2127 7.48761C15.6544 6.49594 13.9169 6.00011 12.0002 6.00011C11.5169 6.00011 11.0419 6.03344 10.5752 6.10011C10.1086 6.16678 9.65022 6.26678 9.20022 6.40011L8.41945 5.61934C9.00022 5.4001 9.59124 5.24209 10.1925 5.14529C10.7938 5.0485 11.3964 5.00011 12.0002 5.00011C14.1707 5.00011 16.156 5.59659 17.956 6.78954C19.756 7.98249 21.0784 9.55268 21.9233 11.5001C21.5656 12.304 21.1127 13.0594 20.5646 13.7665C20.0166 14.4735 19.3643 15.1245 18.6079 15.7193ZM19.7233 21.1386L15.831 17.2578C15.3887 17.454 14.8396 17.6267 14.1839 17.7761C13.5281 17.9254 12.8002 18.0001 12.0002 18.0001C9.81689 18.0001 7.83163 17.4036 6.04445 16.2107C4.25726 15.0177 2.93483 13.4475 2.07715 11.5001C2.4656 10.6168 2.97777 9.78954 3.61367 9.01839C4.24957 8.24724 4.92842 7.60012 5.65022 7.07704L2.86175 4.27704L3.56945 3.56934L20.431 20.4309L19.7233 21.1386ZM6.3579 7.78474C5.78483 8.19242 5.19894 8.72831 4.60022 9.39241C4.00151 10.0565 3.53484 10.7591 3.20022 11.5001C4.03356 13.1834 5.22939 14.5209 6.78772 15.5126C8.34606 16.5043 10.0836 17.0001 12.0002 17.0001C12.5771 17.0001 13.1585 16.944 13.7444 16.8318C14.3303 16.7197 14.7707 16.6065 15.0656 16.4924L13.4348 14.8501C13.2771 14.9386 13.0573 15.0113 12.7752 15.0684C12.4932 15.1254 12.2348 15.154 12.0002 15.154C10.981 15.154 10.1172 14.7998 9.40887 14.0915C8.70054 13.3831 8.34637 12.5193 8.34637 11.5001C8.34637 11.2783 8.3749 11.0296 8.43195 10.754C8.489 10.4783 8.56176 10.2488 8.65022 10.0655L6.3579 7.78474Z' fill='%231C1B1F' /%3E%3C/svg%3E");
    }

    &.enrolled-courses-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.1484 21.7273C11.0254 20.8119 9.78497 20.0895 8.42729 19.56C7.06959 19.0305 5.6433 18.7145 4.14844 18.6119V9.26574C5.67792 9.35547 7.13112 9.72053 8.50804 10.3609C9.88497 11.0013 11.0984 11.7939 12.1484 12.7388C13.1984 11.7939 14.4119 11.0013 15.7888 10.3609C17.1657 9.72053 18.619 9.35547 20.1484 9.26574V18.6119C18.6497 18.7145 17.2225 19.0305 15.8667 19.56C14.5109 20.0895 13.2715 20.8119 12.1484 21.7273ZM12.1484 20.4734C13.1984 19.7029 14.3151 19.0816 15.4984 18.6094C16.6818 18.1372 17.8984 17.8252 19.1484 17.6734V10.3888C17.842 10.6055 16.5949 11.027 15.4071 11.6532C14.2193 12.2795 13.1331 13.085 12.1484 14.0696C11.1638 13.085 10.0776 12.2795 8.88979 11.6532C7.70197 11.027 6.45485 10.6055 5.14844 10.3888V17.6734C6.39844 17.8252 7.6151 18.1372 8.79844 18.6094C9.98177 19.0816 11.0984 19.7029 12.1484 20.4734ZM12.1484 9.18881C11.26 9.18881 10.4994 8.87246 9.86671 8.23976C9.23401 7.60708 8.91766 6.8465 8.91766 5.95804C8.91766 5.06957 9.23401 4.30899 9.86671 3.67631C10.4994 3.04361 11.26 2.72726 12.1484 2.72726C13.0369 2.72726 13.7975 3.04361 14.4302 3.67631C15.0629 4.30899 15.3792 5.06957 15.3792 5.95804C15.3792 6.8465 15.0629 7.60708 14.4302 8.23976C13.7975 8.87246 13.0369 9.18881 12.1484 9.18881ZM12.1491 8.18881C12.7628 8.18881 13.2879 7.97031 13.7244 7.53331C14.1609 7.09633 14.3792 6.57101 14.3792 5.95736C14.3792 5.34371 14.1607 4.81862 13.7237 4.38209C13.2867 3.94554 12.7614 3.72726 12.1478 3.72726C11.5341 3.72726 11.009 3.94576 10.5725 4.38276C10.1359 4.81974 9.91766 5.34506 9.91766 5.95871C9.91766 6.57236 10.1362 7.09746 10.5732 7.53401C11.0101 7.97054 11.5355 8.18881 12.1491 8.18881Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.not-completed-courses-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M21.4711 22.1955L17.9634 18.6878C17.1891 19.3109 16.3298 19.7962 15.3856 20.1436C14.4413 20.4911 13.4333 20.6648 12.3615 20.6648C11.1165 20.6648 9.94652 20.4285 8.85152 19.956C7.75652 19.4835 6.80402 18.8423 5.99402 18.0323C5.18402 17.2223 4.54277 16.2698 4.07027 15.1748C3.59777 14.0798 3.36152 12.9098 3.36152 11.6648C3.36152 10.593 3.53524 9.58496 3.88267 8.64072C4.23011 7.69651 4.71536 6.83721 5.33845 6.06285L2.24805 2.97247L2.96152 2.259L22.1846 21.4821L21.4711 22.1955ZM12.3615 19.6648C13.291 19.6648 14.167 19.5135 14.9894 19.2109C15.8118 18.9084 16.5615 18.4923 17.2384 17.9628L12.8827 13.6071L10.9231 15.5725L7.36537 12.0148L8.07307 11.3071L10.9231 14.1571L12.175 12.8994L6.06345 6.78785C5.53396 7.46478 5.11794 8.21446 4.81537 9.0369C4.51281 9.85933 4.36152 10.7353 4.36152 11.6648C4.36152 13.8814 5.14069 15.7689 6.69902 17.3273C8.25736 18.8856 10.1449 19.6648 12.3615 19.6648ZM19.9865 16.4725L19.2423 15.7282C19.6051 15.1321 19.8824 14.4944 20.074 13.8153C20.2657 13.1362 20.3615 12.4194 20.3615 11.6648C20.3615 9.4481 19.5824 7.5606 18.024 6.00227C16.4657 4.44394 14.5782 3.66477 12.3615 3.66477C11.6115 3.66477 10.8958 3.7606 10.2144 3.95227C9.533 4.14394 8.89422 4.42118 8.29807 4.784L7.55382 4.03977C8.25312 3.60259 9.00706 3.26412 9.81562 3.02437C10.6242 2.78464 11.4728 2.66477 12.3615 2.66477C13.6065 2.66477 14.7765 2.90102 15.8715 3.37352C16.9665 3.84602 17.919 4.48727 18.729 5.29727C19.539 6.10727 20.1803 7.05977 20.6528 8.15477C21.1253 9.24977 21.3615 10.4198 21.3615 11.6648C21.3615 12.5535 21.2417 13.4021 21.0019 14.2107C20.7622 15.0192 20.4237 15.7732 19.9865 16.4725ZM15.0077 11.4878L14.3 10.7801L16.6115 8.46862L17.3192 9.17632L15.0077 11.4878Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.overdue-courses-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.9998 21C10.8909 21 9.85143 20.7913 8.88156 20.374C7.91169 19.9567 7.06426 19.3855 6.33926 18.6605C5.61426 17.9355 5.0431 17.0881 4.62578 16.1182C4.20848 15.1484 3.99983 14.1089 3.99983 13C3.99983 11.891 4.20848 10.8515 4.62578 9.88168C5.0431 8.91181 5.61426 8.06438 6.33926 7.33938C7.06426 6.61438 7.91169 6.04322 8.88156 5.6259C9.85143 5.2086 10.8909 4.99995 11.9998 4.99995C13.1088 4.99995 14.1482 5.2086 15.1181 5.6259C16.088 6.04322 16.9354 6.61438 17.6604 7.33938C18.3854 8.06438 18.9566 8.91181 19.3739 9.88168C19.7912 10.8515 19.9998 11.891 19.9998 13C19.9998 14.1089 19.7912 15.1484 19.3739 16.1182C18.9566 17.0881 18.3854 17.9355 17.6604 18.6605C16.9354 19.3855 16.088 19.9567 15.1181 20.374C14.1482 20.7913 13.1088 21 11.9998 21ZM15.146 16.8538L15.8537 16.1461L12.4998 12.7923V7.99995H11.4998V13.2077L15.146 16.8538ZM5.90753 3.42688L6.61521 4.13458L3.13446 7.61533L2.42676 6.90765L5.90753 3.42688ZM18.0921 3.42688L21.5729 6.90765L20.8652 7.61533L17.3845 4.13458L18.0921 3.42688ZM11.9998 20C13.937 20 15.588 19.3176 16.9527 17.9528C18.3175 16.5881 18.9998 14.9371 18.9998 13C18.9998 11.0628 18.3175 9.41181 16.9527 8.04708C15.588 6.68233 13.937 5.99995 11.9998 5.99995C10.0626 5.99995 8.41169 6.68233 7.04696 8.04708C5.68221 9.41181 4.99983 11.0628 4.99983 13C4.99983 14.9371 5.68221 16.5881 7.04696 17.9528C8.41169 19.3176 10.0626 20 11.9998 20Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.phising-campaigns-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11.8269 20.7273C10.4038 20.7273 9.20192 20.2177 8.22115 19.1984C7.24038 18.1792 6.75 16.9516 6.75 15.5157V10.9388L10.3077 14.5734L9.61923 15.2677L7.75 13.3215V15.5157C7.75 16.667 8.14488 17.6568 8.93463 18.485C9.72436 19.3132 10.6885 19.7273 11.8269 19.7273C12.9654 19.7273 13.9295 19.3132 14.7192 18.485C15.509 17.6568 15.9038 16.667 15.9038 15.5157V12.1484C15.3679 12.0305 14.9263 11.7622 14.5788 11.3436C14.2314 10.925 14.0577 10.4375 14.0577 9.88112C14.0577 9.32471 14.2282 8.834 14.5692 8.409C14.9103 7.984 15.3551 7.71252 15.9038 7.59457V3.72727H16.9038V7.59457C17.4526 7.71252 17.8974 7.984 18.2385 8.409C18.5795 8.834 18.75 9.32471 18.75 9.88112C18.75 10.4375 18.5795 10.926 18.2385 11.3465C17.8974 11.767 17.4526 12.0343 16.9038 12.1484V15.5157C16.9038 16.9516 16.4135 18.1792 15.4327 19.1984C14.4519 20.2177 13.25 20.7273 11.8269 20.7273ZM16.4038 11.2273C16.7769 11.2273 17.0946 11.0962 17.3567 10.834C17.6189 10.5718 17.75 10.2542 17.75 9.88112C17.75 9.50804 17.6189 9.19041 17.3567 8.92822C17.0946 8.66606 16.7769 8.53497 16.4038 8.53497C16.0308 8.53497 15.7131 8.66606 15.451 8.92822C15.1888 9.19041 15.0577 9.50804 15.0577 9.88112C15.0577 10.2542 15.1888 10.5718 15.451 10.834C15.7131 11.0962 16.0308 11.2273 16.4038 11.2273Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.emails-sent-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18.856 20.1215L18.1675 19.433L20.2233 17.333H15.356V16.333H20.2233L18.1675 14.233L18.856 13.5446L22.1444 16.833L18.856 20.1215ZM4.97133 17.333C4.5271 17.333 4.14682 17.1749 3.83048 16.8585C3.51413 16.5422 3.35596 16.1619 3.35596 15.7177V5.94841C3.35596 5.50418 3.51413 5.12389 3.83048 4.80756C4.14682 4.49121 4.5271 4.33303 4.97133 4.33303H17.7406C18.1848 4.33303 18.5651 4.49121 18.8814 4.80756C19.1978 5.12389 19.356 5.50418 19.356 5.94841V11.3561C19.2726 11.3484 19.1893 11.3426 19.106 11.3388C19.0226 11.335 18.9393 11.333 18.856 11.333C18.7726 11.333 18.6893 11.334 18.606 11.3359C18.5226 11.3378 18.4393 11.3427 18.356 11.3503V6.07918L11.256 11.2177L4.35596 6.08496V15.7177C4.35596 15.8971 4.41365 16.0446 4.52903 16.16C4.64442 16.2753 4.79185 16.333 4.97133 16.333H13.3733C13.3656 16.4164 13.3608 16.4997 13.3588 16.583C13.3569 16.6664 13.356 16.7497 13.356 16.833C13.356 16.9164 13.3579 16.9997 13.3617 17.083C13.3656 17.1664 13.3713 17.2497 13.379 17.333H4.97133ZM4.99828 5.33303L11.256 9.99841L17.7021 5.33303H4.99828Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.emails-opened-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.75 3.32342L21.2615 8.37727C21.4077 8.48369 21.5257 8.6151 21.6154 8.7715C21.7051 8.92791 21.75 9.09779 21.75 9.28112V19.5157C21.75 19.976 21.5958 20.3603 21.2875 20.6686C20.9792 20.977 20.5949 21.1311 20.1346 21.1311H5.36538C4.90513 21.1311 4.52083 20.977 4.2125 20.6686C3.90417 20.3603 3.75 19.976 3.75 19.5157V9.28112C3.75 9.09779 3.79487 8.92791 3.8846 8.7715C3.97435 8.6151 4.0923 8.48369 4.23845 8.37727L12.75 3.32342ZM12.75 13.7811L20.55 9.13112L12.75 4.48112L4.95 9.13112L12.75 13.7811ZM12.75 14.9388L4.75 10.1773V19.5157C4.75 19.6952 4.80769 19.8427 4.92308 19.958C5.03846 20.0734 5.18589 20.1311 5.36538 20.1311H20.1346C20.3141 20.1311 20.4615 20.0734 20.5769 19.958C20.6923 19.8427 20.75 19.6952 20.75 19.5157V10.1773L12.75 14.9388Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.links-clicked-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.17285 14.9965L10.5517 11.6696H15.4363L8.17285 5.9619V14.9965ZM13.9094 20.5542L10.5729 13.3696L7.17285 18.1119V3.90035L18.3267 12.6696H12.4382L15.7306 19.7023L13.9094 20.5542Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.data-submitted-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='25' height='25' viewBox='0 0 25 25' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.7501 21.0023C12.5475 21.0023 12.3478 20.9619 12.151 20.8811C11.9543 20.8004 11.7751 20.6811 11.6135 20.5234L4.45395 13.3638C4.29625 13.2023 4.17701 13.0231 4.09625 12.8263C4.01548 12.6295 3.9751 12.4298 3.9751 12.2273C3.9751 12.0247 4.01548 11.8241 4.09625 11.6253C4.17701 11.4266 4.29625 11.2484 4.45395 11.0907L11.6135 3.93112C11.7751 3.76957 11.9543 3.64937 12.151 3.57052C12.3478 3.49169 12.5475 3.45227 12.7501 3.45227C12.9527 3.45227 13.1533 3.49169 13.352 3.57052C13.5507 3.64937 13.7289 3.76957 13.8866 3.93112L21.0462 11.0907C21.2078 11.2484 21.328 11.4266 21.4068 11.6253C21.4857 11.8241 21.5251 12.0247 21.5251 12.2273C21.5251 12.4298 21.4857 12.6295 21.4068 12.8263C21.328 13.0231 21.2078 13.2023 21.0462 13.3638L13.8866 20.5234C13.7289 20.6811 13.5507 20.8004 13.352 20.8811C13.1533 20.9619 12.9527 21.0023 12.7501 21.0023ZM13.1924 19.81L20.3328 12.6696C20.4354 12.567 20.4866 12.4196 20.4866 12.2273C20.4866 12.035 20.4354 11.8875 20.3328 11.785L13.1924 4.64457C13.0898 4.542 12.9424 4.49072 12.7501 4.49072C12.5578 4.49072 12.4104 4.542 12.3078 4.64457L5.1674 11.785C5.06483 11.8875 5.01355 12.035 5.01355 12.2273C5.01355 12.4196 5.06483 12.567 5.1674 12.6696L12.3078 19.81C12.4104 19.9125 12.5578 19.9638 12.7501 19.9638C12.9424 19.9638 13.0898 19.9125 13.1924 19.81ZM12.2501 13.4003H13.2501V8.16957H12.2501V13.4003ZM12.7501 15.6311C12.918 15.6311 13.0626 15.5705 13.1837 15.4494C13.3049 15.3282 13.3655 15.1837 13.3655 15.0157C13.3655 14.8478 13.3049 14.7032 13.1837 14.5821C13.0626 14.4609 12.918 14.4003 12.7501 14.4003C12.5821 14.4003 12.4376 14.4609 12.3164 14.5821C12.1953 14.7032 12.1347 14.8478 12.1347 15.0157C12.1347 15.1837 12.1953 15.3282 12.3164 15.4494C12.4376 15.5705 12.5821 15.6311 12.7501 15.6311Z' fill='%231C1B1F'/%3E%3C/svg%3E");
    }

    &.emails-reported-icon .p-column-title:before {
      content: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 14V6.00001M14 14L20.102 17.487C20.1932 17.5392 20.2965 17.5665 20.4015 17.5662C20.5066 17.566 20.6097 17.5381 20.7007 17.4855C20.7916 17.4328 20.8671 17.3572 20.9196 17.2662C20.9721 17.1753 20.9999 17.0721 21 16.967V3.03301C21 2.92786 20.9724 2.82455 20.92 2.73342C20.8675 2.6423 20.792 2.56656 20.7011 2.51379C20.6102 2.46103 20.5069 2.43309 20.4018 2.43277C20.2966 2.43246 20.1933 2.45978 20.102 2.51201L14 6.00001M14 14H7M14 6.00001H7C5.93913 6.00001 4.92172 6.42143 4.17157 7.17158C3.42143 7.92172 3 8.93914 3 10C3 11.0609 3.42143 12.0783 4.17157 12.8284C4.92172 13.5786 5.93913 14 7 14M7 14L7.757 19.3C7.83599 19.8157 8.11528 20.2793 8.53419 20.5902C8.95309 20.9011 9.47776 21.0341 9.99419 20.9603C10.5106 20.8865 10.977 20.6119 11.2922 20.1962C11.6073 19.7804 11.7456 19.2571 11.677 18.74L11 14H7Z' stroke='black'/%3E%3C/svg%3E");
    }
  }
`;

export const SummaryReportDatatable: React.FC<{
  showBlank: boolean;
  initialLoading: boolean;
}> = ({ showBlank, initialLoading }) => {
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const { can } = usePermission();
  const { canUseFeature } = useFeatureFlag();
  const isBranchAdmin = branchAdminCheck(currentUser, account);
  const navigate = useNavigate();

  const [exportFilters, setExportFilters] = useState<FiltersType | undefined>();
  const [exportSorting, setExportSorting] = useState<
    [string, 'asc' | 'desc'] | null
  >();
  const [forceRefetch, setForceRefetch] = useState<boolean>(false);

  const { skip, take, sort, apiFilters, onFilter, onSort, onPage, setSkip } =
    useTable({ tableName: TableNamesEnum.SUMMARY_REPORTS });

  const [multiSearchValue, setMultiSearchValue] = useState('');
  const debouncedSetMultiSearchValue = useMemo(
    () =>
      debounce((event: FormEvent) => {
        setSkip(0);
        setMultiSearchValue((event.target as HTMLInputElement).value);
      }, 300),
    [],
  );

  const multiSearchFilters = multiSearchValue.length
    ? [
        group(GroupCondition.OR, [
          nested('user', [like('email', multiSearchValue)]),
          nested('user', [like('name', multiSearchValue)]),
        ]),
      ]
    : [];

  const onSuccess = () => {
    setExportFilters([...apiFilters, ...multiSearchFilters]);
    setExportSorting(sort);
  };

  const addSortNulls = (sort: [string, 'asc' | 'desc'] | null) => {
    if (!sort) {
      return undefined;
    }

    const columnName = sort[0];
    const order = sort[1];

    switch (columnName) {
      case 'lastLogin':
        return order === 'asc' ? 'last' : 'first';
      default:
        return undefined;
    }
  };

  const { isLoading, usersSummaryReport, refetch, isFetching } =
    useUsersSummaryReport(
      account?.id || '',
      {
        take,
        skip,
        filters: [...apiFilters, ...multiSearchFilters],
        sort: sort && sort.length > 0 ? [sort.join(',')] : [],
        sortNulls: addSortNulls(sort),
        enabled: !showBlank,
      },
      onSuccess,
    );

  useEffect(() => {
    if (forceRefetch) {
      refetch();
    }
  }, [forceRefetch]);

  const { params } = useQueryParams(getQueryTransformation<{ name: string }>());
  const { state } = useLocation();
  const [isDashboardLink] = useState(state?.isDashboardLink || false);

  const columns = [
    {
      field: 'user.firstName',
      header: t('user'),
      exportName: 'firstName',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => <span>{row?.user?.name}</span>,
    },
    {
      field: 'user.email',
      header: t('generic.email'),
      exportName: 'email',
      sortable: true,
      filterable: false,
      frozen: true,
      render: (row: UsersSummaryReport) => (
        <span>{row?.user?.email ?? <>&mdash;</>}</span>
      ),
    },
    {
      field: 'user.username',
      header: t('reports.columns.username'),
      exportName: 'username',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => <span>{row?.user?.username}</span>,
    },
    {
      field: 'user.language',
      header: t('reports.columns.language'),
      exportName: 'language',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => (
        <span>{row?.user?.language ?? <>&mdash;</>}</span>
      ),
    },
    {
      field: 'user.active',
      header: t('reports.columns.active'),
      exportName: 'active',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => (
        <span>{row?.user?.active ? t('dialog.yes') : t('dialog.no')}</span>
      ),
    },
    {
      field: 'user.branch.name',
      header: t('branch'),
      exportName: 'branch',
      sortable: true,
      filterable: true,
      filters: {
        placeholder: t('filter.branch'),
        field: FilterNamesEnum.USER_BRANCH,
        type: FilterTypeEnum.MULTI_SELECT,
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => {
        const id = uniqueId('branch');
        return (
          <>
            {row?.user?.branch ? (
              <>
                {row?.user?.branch && (
                  <Tooltip
                    position="mouse"
                    target={`.${id}`}
                    content={getBranchName(row.user.branch)
                      .reverse()
                      .join(' / ')}
                  />
                )}
                <div className={id} style={{ minWidth: 'max-content' }}>
                  {row.user.branch?.name}
                </div>
              </>
            ) : (
              <div className="group-row">&#8212;</div>
            )}
          </>
        );
      },
    },
    ...(can(Actions.READ, Subjects.GROUPS) && !isBranchAdmin
      ? [
          {
            field: 'user.groupNames',
            header: t('group'),
            exportName: 'groups',
            sortable: true,
            filterable: true,
            filters: {
              placeholder: t('filter.group'),
              field: FilterNamesEnum.USER_GROUPS,
              type: FilterTypeEnum.MULTI_SELECT,
            },
            className: 'no-padding',
            render: (row: UsersSummaryReport) => {
              return (
                <>
                  {row?.user?.groups.length ? (
                    row.user.groups.map((group) => (
                      <div key={group.id} style={{ minWidth: 'max-content' }}>
                        {group.name}
                      </div>
                    ))
                  ) : (
                    <div className="group-row">&#8212;</div>
                  )}
                </>
              );
            },
          },
        ]
      : []),

    {
      field: 'enrolledCourses',
      headerAriaLabel: t('content.enrolled'),
      header: (
        <Tooltip
          position="top"
          target="th.enrolled-courses-icon"
          content={t('content.enrolled')}
        />
      ),
      exportName: 'enrolledContents',
      iconLabel: t('content.enrolled'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.have.enrolledContent'),
        placeholder: t('generic.select'),
        field: FilterNamesEnum.SUMMARY_HAVE_ENROLLED_COURSES,
        type: FilterTypeEnum.SELECT_YES_NO,
      },
      className: 'icon-column',
      headerClassName: 'icon-column enrolled-courses-icon',
      render: (row: UsersSummaryReport) => row?.enrolledCourses,
    },
    {
      field: 'notCompletedCourses',
      headerAriaLabel: t('content.not.completed'),
      header: (
        <Tooltip
          position="top"
          target="th.not-completed-courses-icon"
          content={t('content.not.completed')}
        />
      ),
      exportName: 'notCompletedContents',
      iconLabel: t('content.not.completed'),
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.have.completedAllContent'),
        placeholder: t('generic.select'),
        field: FilterNamesEnum.SUMMARY_COMPLETED_ALL_COURSES,
        type: FilterTypeEnum.SELECT_YES_NO,
      },
      className: 'icon-column',
      headerClassName: 'icon-column not-completed-courses-icon',
      render: (row: UsersSummaryReport) => row?.notCompletedCourses,
    },
    ...(account?.courseDueDays
      ? [
          {
            field: 'overdueCourses',
            headerAriaLabel: t('content.overDue'),
            header: (
              <Tooltip
                position="top"
                target="th.overdue-courses-icon"
                content={t('content.overDue')}
              />
            ),
            exportName: 'overdueContents',
            iconLabel: t('content.overDue'),
            sortable: true,
            filterable: true,
            filters: {
              label: t('filter.have.overdueContent'),
              placeholder: t('generic.select'),
              field: FilterNamesEnum.SUMMARY_HAVE_OVERDUE_COURSES,
              type: FilterTypeEnum.SELECT_YES_NO,
            },
            className: 'icon-column',
            headerClassName: 'icon-column overdue-courses-icon',
            render: (row: UsersSummaryReport) => row?.overdueCourses ?? 0,
          },
        ]
      : []),
    {
      field: 'phishingCampaigns',
      headerAriaLabel: t('campaigns.running'),
      header: (
        <Tooltip
          position="top"
          target="th.phising-campaigns-icon"
          content={t('campaigns.running')}
        />
      ),
      exportName: 'runningPhishingCampaigns',
      iconLabel: t('campaigns.running'),
      sortable: true,
      filterable: false,
      className: 'icon-column',
      headerClassName: 'icon-column phising-campaigns-icon',
      render: (row: UsersSummaryReport) => row?.phishingCampaigns,
    },
    {
      field: 'emailsSent',
      headerAriaLabel: t('reports.campaigns.sent'),
      header: (
        <Tooltip
          position="top"
          target="th.emails-sent-icon"
          content={t('reports.campaigns.sent')}
        />
      ),
      exportName: 'emailsSent',
      iconLabel: t('reports.campaigns.sent'),
      className: 'icon-column',
      headerClassName: 'icon-column emails-sent-icon',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => row?.emailsSent,
    },
    canUseFeature(Subjects.EMAIL_OPEN_EVENTS)
      ? {
          field: 'emailsOpened',
          headerAriaLabel: t('campaign.emailOpened'),
          header: (
            <Tooltip
              position="top"
              target="th.emails-opened-icon"
              content={t('campaign.emailOpened')}
            />
          ),
          exportName: 'emailsOpened',
          iconLabel: t('campaign.emailOpened'),
          className: 'icon-column',
          headerClassName: 'icon-column emails-opened-icon',
          sortable: true,
          filterable: false,
          render: (row: UsersSummaryReport) => row?.emailsOpened,
        }
      : null,
    {
      field: 'linksClicked',
      headerAriaLabel: t('campaign.clicked'),
      header: (
        <Tooltip
          position="top"
          target="th.links-clicked-icon"
          content={t('campaign.clicked')}
        />
      ),
      exportName: 'clickedLinks',
      iconLabel: t('campaign.clicked'),
      className: 'icon-column',
      headerClassName: 'icon-column links-clicked-icon',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => row?.linksClicked,
    },
    {
      field: 'dataSubmitted',
      headerAriaLabel: t('campaign.submitted'),
      header: (
        <Tooltip
          position="top"
          target="th.data-submitted-icon"
          content={t('campaign.submitted')}
        />
      ),
      exportName: 'submittedData',
      iconLabel: t('campaign.submitted'),
      className: 'icon-column',
      headerClassName: 'icon-column data-submitted-icon',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) => row?.dataSubmitted,
    },
    ...(canUseFeature(Subjects.OUTLOOK_PHISHING_EMAIL_REPORTING)
      ? [
          {
            field: 'emailsReported',
            headerAriaLabel: t('campaign.email.reported'),
            header: (
              <Tooltip
                position="top"
                target="th.emails-reported-icon"
                content={t('campaign.email.reported')}
              />
            ),
            exportName: 'emailsReported',
            iconLabel: t('campaign.email.reported'),
            className: 'icon-column',
            headerClassName: 'icon-column emails-reported-icon',
            sortable: true,
            filterable: false,
            render: (row: UsersSummaryReport) => row?.emailsReported,
          },
        ]
      : []),
    {
      field: 'averageScore',
      header: t('generic.averageScore'),
      exportName: 'averageScore',
      className: 'no-padding',
      sortable: true,
      filterable: false,
      render: (row: UsersSummaryReport) =>
        row.enrolledCourses ? (
          row.averageScore ?? 0
        ) : (
          <div className="group-row">&#8212;</div>
        ),
    },
    {
      field: 'lastAction',
      header: t('generic.lastAction'),
      exportName: 'lastAction',
      className: 'no-padding',
      style: { minWidth: '150px' },
      sortable: true,
      filterable: true,
      filters: {
        type: FilterTypeEnum.MULTI_SELECT,
        options: getCampaignEventStatuses(
          t,
          canUseFeature(Subjects.EMAIL_OPEN_EVENTS),
        ),
      },
      render: (row: UsersSummaryReport) => (
        <>
          {row?.lastAction ? (
            <span>{t(CampaignUserResultMessages[row.lastAction])}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </>
      ),
    },
    {
      field: 'user.companyData.companyName',
      header: t('generic.companyName'),
      exportName: 'companyName',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_COMPANY_NAME,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.companyName'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.companyName ? (
            <span>{row?.user.companyData.companyName}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.country',
      header: t('generic.country'),
      exportName: 'country',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_COUNTRY,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.country'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.country ? (
            <span>{row?.user.companyData.country}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.officeLocation',
      header: t('generic.officeLocation'),
      exportName: 'officeLocation',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_OFFICE_LOCATION,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.officeLocation'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.officeLocation ? (
            <span>{row?.user.companyData.officeLocation}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.department',
      header: t('generic.department'),
      exportName: 'department',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_DEPARTMENT,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.department'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.department ? (
            <span>{row?.user.companyData.department}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.manager',
      header: t('generic.manager'),
      exportName: 'manager',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_MANAGER,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.manager'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.manager ? (
            <span>{row?.user.companyData.manager}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.managerEmail',
      header: t('generic.managerEmail'),
      exportName: 'managerEmail',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_MANAGER_EMAIL,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.managerEmail'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.managerEmail ? (
            <span>{row?.user.companyData.managerEmail}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.jobTitle',
      header: t('generic.jobTitle'),
      exportName: 'jobTitle',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_JOB,
        type: FilterTypeEnum.MULTI_SELECT,
        placeholder: t('filter.jobTitle'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.jobTitle ? (
            <span>{row?.user.companyData.jobTitle}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.companyData.mobilePhone',
      header: t('generic.mobilePhone'),
      exportName: 'mobilePhone',
      sortable: true,
      filterable: true,
      filters: {
        field: FilterNamesEnum.USER_MOBILE_PHONE,
        type: FilterTypeEnum.TEXT,
        placeholder: t('filter.mobilePhone'),
      },
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start">
          {row?.user.companyData?.mobilePhone ? (
            <span>{row?.user.companyData.mobilePhone}</span>
          ) : (
            <div className="group-row">&#8212;</div>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'lastLogin',
      header: t('user.lastLogin'),
      exportName: 'lastLogin',
      sortable: true,
      filterable: true,
      filters: {
        label: t('filter.neverLoggedIn'),
        field: FilterNamesEnum.NEVER_LOGGED_IN,
        type: FilterTypeEnum.SELECT_YES_NO,
        placeholder: t('filter.neverLoggedIn'),
      },
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start" width="150px">
          {row?.lastLogin ? (
            <FormatDate date={row?.lastLogin} format={DateFormats.TIMEDATE} />
          ) : (
            <span>&#8212;</span>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.created',
      header: t('generic.creation'),
      sortable: true,
      filterable: false,
      exportName: 'createdOn',
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start" width="150px">
          {row?.user.created ? (
            <FormatDate
              date={row?.user.created}
              format={DateFormats.DATEONLY}
            />
          ) : (
            <span>&#8212;</span>
          )}
        </FlexContainer>
      ),
    },
    {
      field: 'user.updated',
      header: t('reports.columns.updatedOn'),
      sortable: true,
      filterable: false,
      exportName: 'updatedOn',
      className: 'no-padding',
      render: (row: UsersSummaryReport) => (
        <FlexContainer direction="column" align="flex-start" width="150px">
          {row?.user?.updated ? (
            <FormatDate
              date={row?.user?.updated}
              format={DateFormats.DATEONLY}
            />
          ) : (
            <span>&#8212;</span>
          )}
        </FlexContainer>
      ),
    },
  ].filter(Boolean) as DataTableColumnType[];

  // Set the preselected columns
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const defaultVisibleColumns = columns
    .filter(
      (column) =>
        ![
          'language',
          'active',
          'updatedOn',
          'companyName',
          'country',
          'officeLocation',
          'department',
          'manager',
          'jobTitle',
          'mobilePhone',
        ].includes(column.exportName || column.field),
    )
    .map((column) => column.exportName || column.field);
  const alwaysVisibleColumns = ['firstName'];

  const onRowClick = (event: DataTableRowClickEvent) => {
    if (isLoading) return;
    navigate(RedirectPaths[RedirectPathsEnum.USERS_EDIT](event.data.user.id));
  };

  const toolbar = (
    <DataTableToolbar justify="space-between">
      <FlexContainer
        justify="space-between"
        gap={8}
        align="flex-start"
        wrap="wrap"
      >
        <DataTableFilters
          filters={getFiltersFromColumns(columns)}
          onFilter={onFilter}
          className="flex-initial"
          forcedFilter={
            isDashboardLink &&
            params.filters &&
            !!Object.keys(params.filters).length
              ? params.filters
              : undefined
          }
          tableName={TableNamesEnum.SUMMARY_REPORTS}
        />
        <div className="p-input-icon-left flex-auto min-w-300">
          <InputText
            className="w-full"
            onInput={debouncedSetMultiSearchValue}
            placeholder={t('reports.summary.search')}
            autoComplete="off"
          />
          <i className="pi pi-search" />
        </div>
      </FlexContainer>
      <DataTableAPIColumnsMultiSelect
        columns={columns}
        tableName={TableNamesEnum.SUMMARY_REPORTS}
        visibleColumns={visibleColumns}
        setVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={defaultVisibleColumns}
        alwaysVisibleColumns={alwaysVisibleColumns}
      />
    </DataTableToolbar>
  );

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      <ReportsActionHeader
        type={UsersReportType.SUMMARY}
        visibleColumns={visibleColumns}
        filters={exportFilters}
        sort={exportSorting}
        setForceRefetch={setForceRefetch}
        lastGeneratedAt={usersSummaryReport?.lastGeneratedAt}
        showBlank={showBlank}
      />
      {showBlank ? (
        <BlankPage
          isLoading={initialLoading}
          image={SummaryReportBlankPageImg}
          title={t('reports.summary.blank.heading')}
          description={t('reports.summary.blank.description')}
          mainCtaText={t('reports.summary.blank.cta.create')}
          mainCtaOnClick={() =>
            navigate(RedirectPaths[RedirectPathsEnum.USERS]())
          }
          additionsText={[
            t('reports.summary.blank.additions.1'),
            t('reports.summary.blank.additions.2'),
            t('reports.summary.blank.additions.3'),
          ]}
        />
      ) : (
        <>
          <StyledDataTable
            data={usersSummaryReport?.result.map((x) => ({
              ...x,
              id: x.user.id,
            }))}
            count={usersSummaryReport?.count as number}
            isLoading={isLoading}
            isFetching={isFetching}
            toolbar={toolbar}
            columns={columns}
            visibleColumns={visibleColumns}
            onPage={onPage}
            rows={take}
            skip={skip}
            onSort={onSort}
            sort={sort}
            onRowClick={onRowClick}
            stickyScrollbar
          />
        </>
      )}
    </FlexContainer>
  );
};
